import {
    Briefcase,
    BellRing,
    CalendarClock,
    CalendarRange,
    Car,
    ClipboardCheck,
    ClipboardList,
    LayoutDashboard,
    Package,
    PackageOpen,
    ParkingCircle,
    Receipt,
    ScrollText,
    Settings as SettingsIcon,
    ShieldCheck,
    Stethoscope,
    Truck,
    Users as UsersIcon,
    Wrench,
} from "lucide-react";
import { NavLink } from "react-router-dom";
import { PhaseBadge } from "@/components/PhaseBadge";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

function Item({ to, icon: Icon, label, phase, testid }) {
    const disabled = !!phase;
    const content = (
        <div
            data-testid={testid}
            className={cn(
                "group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                disabled
                    ? "cursor-not-allowed text-zinc-400 dark:text-zinc-600"
                    : "text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800/60 dark:hover:text-zinc-50"
            )}
        >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="flex-1 truncate">{label}</span>
            {phase ? <PhaseBadge phase={phase} /> : null}
        </div>
    );
    if (disabled) return content;
    return (
        <NavLink
            to={to}
            end={to === "/"}
            className={({ isActive }) =>
                cn(
                    "block",
                    isActive &&
                        "[&>div]:bg-brand-redSoft [&>div]:text-brand-redDeep dark:[&>div]:bg-brand-red/10 dark:[&>div]:text-brand-redSoft [&>div]:border-brand-redSoft dark:[&>div]:border-brand-red/40"
                )
            }
        >
            {content}
        </NavLink>
    );
}

function Section({ label, children }) {
    return (
        <div className="px-2">
            <div className="px-3 pt-4 pb-1 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">
                {label}
            </div>
            <div className="flex flex-col gap-0.5">{children}</div>
        </div>
    );
}

export function Sidebar({ onNavigate }) {
    const { t } = useT();
    return (
        <aside
            data-testid="app-sidebar"
            className="flex h-full w-64 flex-col gap-1 border-r border-zinc-200 bg-white py-2 dark:border-zinc-800 dark:bg-zinc-950"
            onClick={onNavigate}
        >
            <div className="px-5 pb-3 pt-3">
                <div className="flex items-center gap-2.5">
                    <span
                        className="grid h-8 w-8 place-items-center rounded-md bg-gradient-to-br from-emerald-500 to-blue-600 text-white"
                        data-testid="sidebar-logo"
                    >
                        <Wrench className="h-4 w-4" strokeWidth={2.5} />
                    </span>
                    <div className="leading-tight">
                        <div className="font-display text-base font-semibold tracking-tight">AutoBrix</div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                            Workshop OS
                        </div>
                    </div>
                </div>
            </div>

            <Section label="Operations">
                <Item to="/" icon={LayoutDashboard} label={t("nav.dashboard")} testid="nav-dashboard" />
                <Item to="/schedule" icon={CalendarRange} label="Schedule" testid="nav-schedule" />
                <Item to="/appointments" icon={CalendarClock} label="Appointments" testid="nav-appointments" />
                <Item to="/jobs" icon={ClipboardList} label="Job cards" testid="nav-jobs" />
                <Item to="/inspections" icon={ClipboardCheck} label="Inspections" testid="nav-inspections" />
            </Section>

            <Section label="Workshop">
                <Item to="/customers" icon={Briefcase} label="Customers" testid="nav-customers" />
                <Item to="/vehicles" icon={Car} label="Vehicles" testid="nav-vehicles" />
                <Item to="/bays" icon={ParkingCircle} label="Bays" testid="nav-bays" />
                <Item to="/technicians" icon={Wrench} label="Technicians" testid="nav-technicians" />
                <Item to="/tech-data" icon={Stethoscope} label="Service Specs" testid="nav-tech-data" />
            </Section>

            <Section label="Inventory & Procurement">
                <Item to="/inventory" icon={Package} label="Inventory" testid="nav-inventory" />
                <Item to="/suppliers" icon={Truck} label="Suppliers" testid="nav-suppliers" />
                <Item to="/purchase-orders" icon={PackageOpen} label="Purchase orders" testid="nav-purchase-orders" />
            </Section>

            <Section label="Billing">
                <Item to="/estimates" icon={ScrollText} label="Estimates" testid="nav-estimates" />
                <Item to="/invoices" icon={Receipt} label="Invoices" testid="nav-invoices" />
                <Item to="/payments" icon={ParkingCircle} label="Payments" testid="nav-payments" />
            </Section>

            <Section label="Administration">
                <Item to="/branches" icon={ShieldCheck} label={t("nav.branches")} testid="nav-branches" />
                <Item to="/users" icon={UsersIcon} label={t("nav.users")} testid="nav-users" />
                <Item to="/notifications" icon={BellRing} label="Notifications" testid="nav-notifications" />
                <Item to="/reminders" icon={CalendarClock} label="Reminders" testid="nav-reminders" />
                <Item to="/audit" icon={ScrollText} label={t("nav.audit")} testid="nav-audit" />
                <Item to="/settings" icon={SettingsIcon} label={t("nav.settings")} testid="nav-settings" />
            </Section>
        </aside>
    );
}
